import { Box, Paper } from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useIsMobile from "../Hooks/useIsMobile";
import apiService from "../Services/api.service";
import CustomButton from "../components/Button";
import CustomCardList from "../components/Cards";
import CustomDatePicker from "../components/CustomDatePicker";
import PageLoader from "../components/Loader";
import Title from "../components/PageTitle";
import SearchableDropdown from "../components/SearchableDropdown";
import CustomTable from "../components/Table";
import TestTypeSelect, { getTestTypeLabel } from "../components/TestTypeSelect";
import ScanThumbnails from "../components/ScanThumbnails";
import FindingsCell from "../components/FindingsCell";
import { useToast } from "../components/Toast";

const parseJson = (value, fallback) => {
  try {
    return JSON.parse(value) ?? fallback;
  } catch {
    return fallback;
  }
};

// Keys come from the API (tests.source.type or derived); labels are product names.
const SCAN_SOURCES = {
  AIR: { label: "Air", color: "#874EF6" },
  ENGAGE: { label: "Engage", color: "#2544a1" },
  PATIENT_APP: { label: "Care", color: "#002446" },
  WEB_SCAN: { label: "Web scan", color: "#0a7d5a" },
  COPILOT: { label: "cOpilot", color: "#b06b9d" },
  NOVA: { label: "Nova", color: "#c2410c" },
  SCANKIT: { label: "scanKit", color: "#555" },
};

const SourceCell = ({ source, kioskName }) => {
  const { label, color } = SCAN_SOURCES[source] || { label: source || "--", color: "#888" };
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center" }}>
      <span style={{ fontWeight: "bold", color }}>{label}</span>
      {source === "AIR" && kioskName && kioskName !== "N/A" && (
        <span style={{ fontSize: "0.75rem", color: "#888" }}>{kioskName}</span>
      )}
    </span>
  );
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "--";

const Scans = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const Toast = useToast();
  const token = localStorage.getItem("token");
  const [searchParams, setSearchParams] = useSearchParams();

  const [allDoctors, setAllDoctors] = useState([]);
  const [allClinics, setAllClinics] = useState([]);
  const [clinics, setClinics] = useState(parseJson(searchParams.get("clinic"), []));
  const [doctors, setDoctors] = useState(parseJson(searchParams.get("doctor"), []));
  const [testType, setTestType] = useState(searchParams.get("scanType") || "");
  // Default to the last 30 days: an unfiltered list has to scan every test (a few seconds).
  const [startDate, setStartDate] = useState(
    searchParams.has("start") ? searchParams.get("start") : dayjs().subtract(30, "day").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(searchParams.get("end") || "");
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [rowsPerPage, setRowsPerPage] = useState(Number(searchParams.get("limit")) || 25);

  const [scans, setScans] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Preview: hover (or click) the row's Preview button; thumbnails fetched once per scan.
  // Closing is delayed so the pointer can travel from the button into the card.
  const [preview, setPreview] = useState(null); // { row, anchorEl }
  const [previewImages, setPreviewImages] = useState(null);
  const thumbnailCache = useRef(new Map());
  const closeTimer = useRef(null);

  const showPreview = async (row, event) => {
    clearTimeout(closeTimer.current);
    if (preview?.row.tid === row.tid) return;
    setPreview({ row, anchorEl: event.currentTarget });
    const cached = thumbnailCache.current.get(row.tid);
    if (cached) {
      setPreviewImages(cached);
      return;
    }
    setPreviewImages(null);
    try {
      const images = await apiService.fetchScanImages(token, row.tid);
      thumbnailCache.current.set(row.tid, images);
      setPreviewImages(images);
    } catch (error) {
      setPreviewImages([]);
      Toast.error(error.response?.data?.message || "Could not load preview");
    }
  };

  const hidePreview = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPreview(null), 250);
  };
  const keepPreview = () => clearTimeout(closeTimer.current);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const res = await apiService.fetchDoctorAndClinics("", "", token);
        setAllDoctors(res.data.doctors);
        setAllClinics(res.data.clinics);
      } catch (error) {
        Toast.error("Error while fetching doctors and clinics");
      }
    };
    loadDropdowns();
  }, []);

  useEffect(() => {
    setSearchParams({
      clinic: JSON.stringify(clinics.map(({ _id, name }) => ({ _id, name }))),
      doctor: JSON.stringify(doctors.map(({ _id, name }) => ({ _id, name }))),
      scanType: testType,
      start: startDate,
      end: endDate,
      page,
      limit: rowsPerPage,
    });

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiService.listScans(token, page, rowsPerPage, {
          clinicIds: clinics.map((c) => c._id),
          doctorIds: doctors.map((d) => d._id),
          testType,
          startDate,
          endDate,
        });
        if (cancelled) return;
        setScans(res.scans.map((scan, index) => ({ ...scan, id: (page - 1) * rowsPerPage + index + 1 })));
        setTotal(res.total);
      } catch (error) {
        if (!cancelled) Toast.error(error.response?.data?.message || "Error while fetching scans");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [clinics, doctors, testType, startDate, endDate, page, rowsPerPage]);

  // Any filter change goes back to page 1.
  const withPageReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const openScan = (row) => {
    navigate(`/dashboard/user/test/${row.tid}`, {
      state: {
        tid: row.tid,
        detections: row.detections,
        softTissueDetections: row.softTissueDetections,
        testType: row.testType,
        uid: row.uid,
        name: row.patientName,
        kioskId: row.kioskId,
        doctor: row.doctorName,
      },
    });
  };

  const columns = [
    {
      field: "date",
      headerName: "Date",
      align: "center",
      renderCell: (value) => <span style={{ whiteSpace: "nowrap" }}>{formatDate(value)}</span>,
    },
    { field: "testType", headerName: "Scan type", align: "center", renderCell: getTestTypeLabel },
    { field: "clinicName", headerName: "Clinic", align: "center" },
    { field: "doctorName", headerName: "Doctor", align: "center" },
    {
      field: "source",
      headerName: "Source",
      align: "center",
      renderCell: (source, row) => <SourceCell source={source} kioskName={row.kioskName} />,
    },
    { field: "patientName", headerName: "Patient", align: "center" },
    {
      field: "findingsCount",
      headerName: "Findings",
      align: "center",
      renderCell: (count, row) => <FindingsCell count={count} names={row.findingNames} />,
    },
  ];

  const fields = [
    { label: "Date", key: "date" },
    { label: "Clinic", key: "clinicName" },
    { label: "Source", key: "source" },
    { label: "Patient", key: "patientName" },
    { label: "Findings", key: "findingsCount" },
  ];

  return (
    <Paper sx={{ padding: 2 }}>
      <Title text="Scans" />

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
        <SearchableDropdown
          width={isMobile ? "100%" : 1 / 3}
          label="Clinics"
          options={allClinics}
          selectedOptions={clinics}
          onChange={withPageReset(setClinics)}
          getOptionLabel={(option) => option.name}
          getOptionKey={(option) => option._id}
        />
        <SearchableDropdown
          width={isMobile ? "100%" : 1 / 3}
          label="Doctors"
          options={allDoctors}
          selectedOptions={doctors}
          onChange={withPageReset(setDoctors)}
          getOptionLabel={(option) => option.name}
          getOptionKey={(option) => option._id}
        />
        <TestTypeSelect value={testType} onChange={withPageReset(setTestType)} />
      </Box>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} mt={2} mb={2}>
        <CustomDatePicker
          selectedDate={startDate ? new Date(startDate) : null}
          handleDateChange={(date) =>
            withPageReset(setStartDate)(date ? dayjs(date).format("YYYY-MM-DD") : "")
          }
          label="Start Date"
          isDocEditable={true}
        />
        <CustomDatePicker
          selectedDate={endDate ? new Date(endDate) : null}
          handleDateChange={(date) =>
            withPageReset(setEndDate)(date ? dayjs(date).format("YYYY-MM-DD") : "")
          }
          label="End Date"
          isDocEditable={true}
        />
        <Box flex={1} display="flex" justifyContent="flex-end">
          <CustomButton
            text="Clear filter"
            variant="outlined"
            onClick={() => {
              setClinics([]);
              setDoctors([]);
              setTestType("");
              setStartDate("");
              setEndDate("");
              setPage(1);
            }}
          />
        </Box>
      </Box>

      {isMobile ? (
        <CustomCardList
          page={page - 1}
          rowsPerPage={rowsPerPage}
          totalCount={total}
          onPageChange={(event, newPage) => setPage(newPage + 1)}
          onRowsPerPageChange={(event) => withPageReset(setRowsPerPage)(parseInt(event.target.value, 10))}
          fields={fields}
          data={scans}
          onCardClick={openScan}
        />
      ) : (
        <CustomTable
          columns={columns}
          data={scans}
          onRowClick={openScan}
          actions={[
            {
              Component: CustomButton,
              props: { text: "Preview", width: "100px", height: "40px", variant: "outlined" },
              onClick: showPreview,
              onMouseEnter: showPreview,
              onMouseLeave: hidePreview,
            },
            {
              Component: CustomButton,
              props: { text: "View", width: "90px", height: "40px" },
              onClick: openScan,
            },
          ]}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          totalCount={total}
          onPageChange={(event, newPage) => setPage(newPage + 1)}
          onRowsPerPageChange={(event) => withPageReset(setRowsPerPage)(parseInt(event.target.value, 10))}
        />
      )}
      <ScanThumbnails
        anchorEl={preview?.anchorEl || null}
        images={previewImages}
        onMouseEnter={keepPreview}
        onMouseLeave={hidePreview}
        onOpen={() => preview && openScan(preview.row)}
      />
      {loading && <PageLoader />}
    </Paper>
  );
};

export default Scans;
